import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'

/**
 * `useDropdownOptions` — QA follow-up (2026-07-16): `Dashboard.tsx`'s
 * seller and campaign dropdowns were two copy-pasted `useQuery` +
 * `useMemo` blocks (fetch a list, map it down to `{id, name}`). Extracted
 * once both existed, since a third dropdown (the next filter someone adds)
 * would otherwise very likely become a third copy instead of reusing either.
 */
export interface DropdownOption {
  id: string
  name: string
}

interface UseDropdownOptionsArgs<TResponse, TItem> {
  queryKey: readonly unknown[]
  url: string
  /** Pulls the raw item array out of the API response envelope. */
  selectItems: (response: TResponse) => ReadonlyArray<TItem>
  /** Projects one raw item down to the `{id, name}` the <select> needs. */
  mapOption: (item: TItem) => DropdownOption
  staleTime?: number
}

export function useDropdownOptions<TResponse, TItem>(
  args: UseDropdownOptionsArgs<TResponse, TItem>,
): { options: ReadonlyArray<DropdownOption>; isLoading: boolean } {
  const query = useQuery<TResponse>({
    queryKey: args.queryKey as unknown[],
    queryFn: async () => api<TResponse>(args.url),
    staleTime: args.staleTime ?? 5 * 60 * 1000,
  })

  // Only `query.data` changing should recompute — `selectItems`/`mapOption`
  // are inline closures at the (few) call sites, same posture as the two
  // duplicated blocks this hook replaces (no react-hooks lint rule is
  // configured in this project to require them in the dep array).
  const options = useMemo<ReadonlyArray<DropdownOption>>(() => {
    if (query.data === undefined) return []
    return args.selectItems(query.data).map(args.mapOption)
  }, [query.data])

  return { options, isLoading: query.isLoading }
}
